/**
 * Send the main balance out to an external USDT address on Polygon.
 *
 * ## Why this page is deliberately narrow
 *
 * Every other outbound path on the platform has a customer behind it: their balance, their
 * address, their request. This one has an operator and a text field, and what leaves is the
 * platform's own coin. There is no reversal, no counterparty to ask and no support ticket -
 * once a chain accepts a transaction, the coin belongs to whoever controls that address.
 *
 * So the page does one thing on one rail. **USDT on Polygon**, fixed server-side, with no
 * network picker anywhere on the screen. That absence is the safety feature: every EVM chain
 * shares Ethereum's address format, so a Polygon address pasted while "Ethereum" is selected
 * passes every shape check ever written and sends the coin somewhere nobody controls. A
 * dropdown cannot be made safe here; not having one can.
 *
 * ## What the operator has to do that feels redundant, and is not
 *
 * Retyping the last four characters of the destination. The mistake this catches is not a
 * typo - nobody hand-types an address - it is pasting the *wrong* clipboard entry, which
 * looks entirely correct and is the failure that actually happens. Typing the tail forces a
 * second look at the end of the string that was actually pasted.
 *
 * ## What the numbers mean
 *
 * The available figure comes from the custodian itself, not from our own tables, because the
 * custodian's number is the one that decides whether a send succeeds. A dash rather than a
 * zero means the custodian listed no balance for this rail at all - a different fact from
 * holding none of it, and usually a sign the account or the rail is not what was expected.
 *
 * "Sent" is not "confirmed". The custodian accepting a payout means it is broadcasting, and
 * the transaction hash - when there is one yet - is the only part of this a third party can
 * check.
 */
import { useCallback, useEffect, useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type { TreasuryPayoutPreview, TreasuryPayoutResult } from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, Field, Input, LoadingState } from '../components/ui.js';

export function TreasuryPayoutPage(): JSX.Element {
  const { api } = useAuth();
  const [preview, setPreview] = useState<TreasuryPayoutPreview | null>(null);
  const [result, setResult] = useState<TreasuryPayoutResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [confirmTail, setConfirmTail] = useState('');
  /*
   * The custodian's payout 2FA code, when the account has it switched on.
   *
   * Typed at the moment of sending and kept nowhere: the platform stores no 2FA secret, so
   * a send always needs a human present. It is cleared as soon as the request returns,
   * because a code left sitting in a field is a code somebody can send with.
   */
  const [twoFactorCode, setTwoFactorCode] = useState('');
  /*
   * The second click is the confirmation, and the button says what it is about to do before
   * it does it. A modal would be easy to dismiss by reflex; a button whose label changes to
   * name the amount and the destination has to be read.
   */
  const [armed, setArmed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPreview(await api.treasuryPayoutPreview());
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  // Any edit disarms. Otherwise an operator could arm on one amount, change it, and have the
  // second click send a figure the confirmation never showed them.
  function edit(setter: (value: string) => void): (value: string) => void {
    return (value: string) => {
      setArmed(false);
      setResult(null);
      setter(value);
    };
  }

  async function send(): Promise<void> {
    if (!armed) {
      setArmed(true);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const code = twoFactorCode.trim();
      const sent = await api.treasuryPayoutSend({
        amount: amount.trim(),
        address: address.trim(),
        confirmTail: confirmTail.trim(),
        ...(code.length === 0 ? {} : { twoFactorCode: code }),
      });
      setResult(sent);
      setArmed(false);
      // Clear the destination but keep nothing that could be re-sent by a stray click.
      setAmount('');
      setAddress('');
      setConfirmTail('');
      setTwoFactorCode('');
      await load();
    } catch (cause) {
      setError(messageOf(cause));
      setArmed(false);
      // A code that reached the custodian is spent whether or not it was accepted. Clearing
      // it forces a fresh one rather than a second attempt with a number already burned.
      setTwoFactorCode('');
    } finally {
      setSending(false);
    }
  }

  const blocked = preview !== null && preview.blocker !== null;
  const ready =
    amount.trim().length > 0 && address.trim().length > 0 && confirmTail.trim().length >= 4;

  return (
    <div>
      <header className="page-head">
        <h1 className="page-head__title">Send USDT out</h1>
        <p className="page-head__sub">
          Moves the main balance to an outside wallet on the Polygon network. This leaves the
          platform and cannot be undone, so check the address twice before you send.
        </p>
      </header>

      {error !== null && (
        <div style={{ marginBottom: 16 }}>
          <ErrorBox message={error} />
        </div>
      )}

      {result !== null && (
        <div style={{ marginBottom: 16 }}>
          <Card title={result.status === 'confirmed' ? 'Sent and confirmed' : 'Sent'}>
            <p>
              {result.amount} {result.currency} on {result.network}.
            </p>
            <p className="muted">
              {result.status === 'confirmed'
                ? 'Your custodian reports this one as finished.'
                : 'Your custodian has accepted it and is sending it now. It is on its way, not yet settled.'}
            </p>
            <p className="muted">
              Reference {result.providerReference}
              {result.txHash === null ? '' : ` · transaction ${result.txHash}`}
            </p>
          </Card>
        </div>
      )}

      <Card
        title="Available to send"
        actions={preview === null ? undefined : <Badge tone="neutral">{preview.provider}</Badge>}
      >
        {loading ? (
          <LoadingState label="Asking your custodian what it is holding..." />
        ) : preview === null ? (
          <p className="muted">Nothing to show yet.</p>
        ) : (
          <>
            <p style={{ fontSize: 24, margin: '4px 0 8px' }}>
              {preview.available === null ? 'Not stated' : `${preview.available} ${preview.currency}`}
            </p>
            <p className="muted">
              {preview.available === null
                ? `Your custodian did not list a ${preview.currency} balance on ${preview.network}. That is not the same as holding none: check the rail is the one you expect before sending.`
                : `Held on ${preview.network}. This is your custodian's own figure, read just now.`}
            </p>
            {preview.blocker !== null && (
              <p className="muted" style={{ marginTop: 8 }}>
                {preview.blocker}
              </p>
            )}
          </>
        )}
      </Card>

      <div style={{ marginTop: 16 }}>
        <Card title="Send to an outside wallet">
          <Field label="Amount" htmlFor="payout-amount">
            <Input
              id="payout-amount"
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(event) => edit(setAmount)(event.target.value)}
              disabled={blocked || sending}
            />
          </Field>

          <Field label="Destination address (Polygon)" htmlFor="payout-address">
            <Input
              id="payout-address"
              // Never autofilled and never remembered: this field decides where real coin
              // goes, and a helpfully recalled previous value is exactly the wrong help.
              autoComplete="off"
              spellCheck={false}
              placeholder="0x…"
              value={address}
              onChange={(event) => edit(setAddress)(event.target.value)}
              disabled={blocked || sending}
            />
          </Field>

          <Field label="Retype the last 4 characters of that address" htmlFor="payout-tail">
            <Input
              id="payout-tail"
              autoComplete="off"
              spellCheck={false}
              maxLength={8}
              placeholder="e.g. 4f2a"
              value={confirmTail}
              onChange={(event) => edit(setConfirmTail)(event.target.value)}
              disabled={blocked || sending}
            />
          </Field>

          <Field label="Authenticator code (if your wallet asks for one)" htmlFor="payout-2fa">
            <Input
              id="payout-2fa"
              inputMode="numeric"
              autoComplete="one-time-code"
              spellCheck={false}
              maxLength={10}
              placeholder="6-digit code"
              value={twoFactorCode}
              onChange={(event) => edit(setTwoFactorCode)(event.target.value)}
              disabled={blocked || sending}
            />
          </Field>

          <p className="muted" style={{ marginBottom: 12 }}>
            Leave the code blank if your wallet does not ask for one. If it does and you skip
            it, the transfer is held and then cancelled automatically about an hour later.
          </p>

          <p className="muted" style={{ marginBottom: 12 }}>
            Polygon only. Sending to an address on another network will lose the coin, and
            nobody can recover it.
          </p>

          <Button
            variant={armed ? 'danger' : 'default'}
            loading={sending}
            disabled={blocked || !ready}
            onClick={() => void send()}
          >
            {armed
              ? `Confirm: send ${amount.trim()} USDT to …${address.trim().slice(-6)}`
              : 'Send'}
          </Button>

          {armed && (
            <p className="muted" style={{ marginTop: 8 }}>
              Read the address once more. Clicking again sends it, and it cannot be brought
              back.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
